import { isNarrationPart, type GroupedPart, type MessagePart } from "./utils";
import type { ToolPartBase } from "./ToolComponents";
import { resolveToolDisplay } from "./toolDisplay";
import { isActionablePendingApproval } from "./approval/approvalGate";
import type { McpPrefixMeta } from "./mcpToolIcon";

/**
 * Chain-of-thought header text and grouping. Tool parts defer entirely to
 * ``resolveToolDisplay`` (the single source of truth for tool display); this
 * module only adds the reasoning-part specifics — the headline extracted from
 * the reasoning text — and the running/done/error status of a chain.
 */

export interface ReasoningTexts {
  during: string;
  after: string;
}

const REASONING_TEXTS: ReasoningTexts = {
  during: "Thinking...",
  after: "Thought",
};

export const ACTIVE_TOOL_STATES: ReadonlySet<string> = new Set([
  "input-streaming",
  "input-available",
  "running",
]);

/**
 * Whether an interrupted assistant turn produced anything the worker persists:
 * non-empty text, or a tool call that already got a result. A call still in an
 * active state was cut off before returning and is dropped server-side, so it
 * doesn't count. Used to decide whether a stop should keep the turn or clear it.
 */
export function hasPersistableTurnContent(parts: MessagePart[]): boolean {
  return parts.some((part) => {
    if (part.type === "text") return part.text.trim() !== "";
    if (part.type.startsWith("tool-") && "state" in part) {
      return !ACTIVE_TOOL_STATES.has((part as ToolPartBase).state);
    }
    return false;
  });
}

/**
 * Extract the last "headline" line from a markdown string. Matches either a
 * `# `/`## ` heading or a line whose entire content is `**bold**` (common
 * section-marker pattern in agent reasoning that Streamdown doesn't render
 * as a real heading).
 */
function lastHeadline(text: string): string | null {
  if (!text) return null;
  const lines = text.split("\n");
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    const heading = line.match(/^##? +(.+?)\s*$/);
    if (heading) return heading[1].trim();
    const bold = line.match(/^\*\*(.+?)\*\*$/);
    if (bold) return bold[1].trim();
  }
  return null;
}

const TITLE_MAX = 56;

export function reasoningTitle(
  text: string,
  isStreaming = false
): string | null {
  const headline = lastHeadline(text);
  if (headline) return headline;
  if (isStreaming) return null;
  const firstLine = text.trim().split("\n")[0] ?? "";
  const opening = firstLine.split(/(?<=[.!?])\s/)[0] ?? "";
  const plain = opening
    .replace(/[*_`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return null;
  const clipped =
    plain.length <= TITLE_MAX
      ? plain
      : `${plain.slice(0, plain.lastIndexOf(" ", TITLE_MAX)).trim()}…`;
  return clipped.replace(/[.:,;]$/, "");
}

/**
 * Whether the turn has moved on to its answer: text is the last thing it has
 * to show. Text is always the answer — narration arrives tagged as narration,
 * never as text — so this needs no guess about where the answer starts. A tool
 * or thought after the text means work resumed, and the turn is working again.
 */
export function answerStarted(parts: GroupedPart[]): boolean {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part.type === "text") return part.text.trim() !== "";
    if (part.type === "reasoning" || part.type.startsWith("tool-"))
      return false;
  }
  return false;
}

/** The agent's latest narration, shown under a working chain's header. */
export function getChainPreview(parts: GroupedPart[]): string | null {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part.type === "reasoning" && isNarrationPart(part)) {
      return part.text.trim() || null;
    }
  }
  return null;
}

export function isChainablePart(
  part: GroupedPart,
  mcpPrefixes?: Map<string, McpPrefixMeta>
): boolean {
  if ("state" in part && isActionablePendingApproval(part)) {
    return false;
  }
  if (part.type === "reasoning") return true;
  if (!part.type.startsWith("tool-")) return false;
  return resolveToolDisplay(part as ToolPartBase, mcpPrefixes).chainable;
}

function lookupTexts(
  part: GroupedPart,
  mcpPrefixes?: Map<string, McpPrefixMeta>,
  allParts?: MessagePart[],
  reasoningTexts: ReasoningTexts = REASONING_TEXTS
): ReasoningTexts {
  if (part.type === "reasoning") {
    const streaming = "state" in part && part.state === "streaming";
    const title = reasoningTitle(part.text, streaming);
    if (title) return { during: title, after: title };
    return reasoningTexts;
  }
  if (part.type.startsWith("tool-")) {
    const info = resolveToolDisplay(
      part as ToolPartBase,
      mcpPrefixes,
      allParts
    );
    const during = info.messageStart;
    const after = isPartErrored(part) ? info.messageError : info.messageEnd;
    return { during, after };
  }
  return reasoningTexts;
}

function isPartInProgress(part: GroupedPart): boolean {
  if (part.type === "reasoning") {
    return "state" in part && part.state === "streaming";
  }
  if (part.type.startsWith("tool-") && "state" in part) {
    return ACTIVE_TOOL_STATES.has((part as ToolPartBase).state);
  }
  return false;
}

function isPartErrored(part: GroupedPart): boolean {
  if (part.type.startsWith("tool-") && "state" in part) {
    return (part as ToolPartBase).state === "error";
  }
  return false;
}

/**
 * Returns the text to show on the Chain of Thought collapsible header:
 * the first in-progress step's `during` text, or — once all are done —
 * the last step's `after` text.
 */
export function getChainHeaderText(
  parts: GroupedPart[],
  mcpPrefixes?: Map<string, McpPrefixMeta>,
  allParts?: MessagePart[],
  reasoningTexts: ReasoningTexts = REASONING_TEXTS
): string {
  for (const part of parts) {
    if (isPartInProgress(part))
      return lookupTexts(part, mcpPrefixes, allParts, reasoningTexts).during;
  }
  const last = parts[parts.length - 1];
  return last
    ? lookupTexts(last, mcpPrefixes, allParts, reasoningTexts).after
    : reasoningTexts.after;
}

export function getChainHeaderStatus(
  parts: GroupedPart[]
): "running" | "done" | "error" {
  if (parts.some(isPartInProgress)) return "running";
  if (parts.some(isPartErrored)) return "error";
  return "done";
}
